import React, { ReactElement, useCallback, useMemo, useState } from 'react';

import { WFA_DASHBOARD_SUB_TAB_CONTENT_DAID } from 'data-automation-ids/WfaDashboardAutomationIds';
import { BusinessUnitWebSdkTable } from 'src/apps/dashboard/components/business-unit-web-sdk-table/BusinessUnitWebSdkTable';
import { BusinessUnitWfaTable } from 'src/apps/dashboard/components/business-unit-wfa-table/BusinessUnitWfaTable';
import { useShowOnlyLastReportToggle } from 'src/apps/dashboard/hooks/useShowOnlyLastReportToggle';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';

import { createBusinessUnitSubTabOptions } from '../helpers/TabNavigationHelpers';
import { BusinessUnitSubTabId } from '../types/DashboardTypes';

interface UseBusinessUnitTabsParams {
	businessUnitName: string;
	effectiveFilters: IFilterOptions;
}

interface UseBusinessUnitTabsReturn {
	activeTabIndex: number;
	setActiveTabIndex: (index: number) => void;
	tabs: Array<{
		id: string;
		tablistId: string;
		ref: null;
		isActive: boolean;
		handleKeyDown: () => void;
		children: string;
		ariaControls: string;
		ariaLabel: string;
		onActivate: () => void;
		dataAutomationId: string;
	}>;
	tabPanels: Array<{
		id: string;
		ariaLabelledBy: string;
		isActive: boolean;
		children: ReactElement;
	}>;
}

export const useBusinessUnitTabs = ({
	businessUnitName,
	effectiveFilters
}: UseBusinessUnitTabsParams): UseBusinessUnitTabsReturn => {
	const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
	const { showOnlyLastReport, showOnlyLastReportToggle } = useShowOnlyLastReportToggle();

	const renderTabContent = useCallback(
		(subTabId: BusinessUnitSubTabId, ariaLabel: string): ReactElement => {
			switch (subTabId) {
				case BusinessUnitSubTabId.WEB_SDKS:
					return (
						<div
							className="web-sdks-content"
							data-automation-id="web-sdks-content"
							role="region"
							aria-label={ariaLabel}
						>
							<BusinessUnitWebSdkTable
								dashboardFilters={effectiveFilters}
								unitIdLabel={businessUnitName}
								showOnlyLastReport={showOnlyLastReport}
								topRowActions={showOnlyLastReportToggle}
							/>
						</div>
					);
				case BusinessUnitSubTabId.WEB_FLOW_ANALYZER:
					return (
						<div
							className="web-flow-analyzer-content"
							data-automation-id="web-flow-analyzer-content"
							role="region"
							aria-label={ariaLabel}
						>
							<BusinessUnitWfaTable
								dashboardFilters={effectiveFilters}
								unitIdLabel={businessUnitName}
							/>
						</div>
					);
				default:
					return (
						<div className="default-tab-content">
							<p>Content for this tab is not yet implemented.</p>
						</div>
					);
			}
		},
		[businessUnitName, effectiveFilters, showOnlyLastReport, showOnlyLastReportToggle]
	);

	const { tabs, tabPanels } = useMemo(() => {
		const subTabOptions = createBusinessUnitSubTabOptions();

		const tabs = subTabOptions.map((option, index) => ({
			id: `tab-${option.id}`,
			tablistId: 'business-unit-tabs',
			ref: null,
			isActive: index === activeTabIndex,
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			handleKeyDown: () => {},
			children: option.title,
			ariaControls: `panel-${option.id}`,
			ariaLabel: option.title,
			onActivate: () => setActiveTabIndex(index),
			dataAutomationId: `tab-${option.id}`
		}));

		const tabPanels = subTabOptions.map((option, index) => ({
			id: `panel-${option.id}`,
			ariaLabelledBy: `panel-label-${option.id}`,
			isActive: index === activeTabIndex,
			children: (
				<div className="sub-tab-content" data-automation-id={WFA_DASHBOARD_SUB_TAB_CONTENT_DAID}>
					<span id={`panel-label-${option.id}`} className="sr-only">
						{option.title}
					</span>
					{renderTabContent(option.id as BusinessUnitSubTabId, option.title)}
				</div>
			)
		}));

		return { tabs, tabPanels };
	}, [activeTabIndex, renderTabContent]);

	return {
		activeTabIndex,
		setActiveTabIndex,
		tabs,
		tabPanels
	};
};
